import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Search, CheckCircle2, ScanLine, Loader2, Truck, User, ChevronDown, Plus } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';

// PR-D2 — log a dumper load in ~30 seconds. Type the plate -> truck + driver +
// last client + rate auto-fill. Enter net weight -> tons + revenue shown live.
// Save. Truck/client stay sticky for the next ticket. Revenue is COMPUTED, not
// invoiced.

interface ClientRow {
  client_id: string;
  name_en: string;
  contract_id: string | null;
  rate_per_ton_sar: string | null;
  load_type: string | null;
}
interface ResolveResp {
  vehicle: { id: string; plateEn: string; vehicleType: string } | null;
  driver: { id: string; name: string | null } | null;
  lastClient: { id: string; name: string; contractId: string | null; ratePerTonSar: string | null; loadType: string | null } | null;
}
interface PlateSuggestion {
  id: string;
  plateEn: string;
  code: string;
  vehicleType: string;
  driverName: string | null;
  lastClient: string | null;
  lastTripDate: string | null;
  loadCount: number;
}

const inputCls = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm';
const today = () => new Date().toISOString().slice(0, 10);

/** Current local time in the shape a datetime-local input expects. */
const nowLocal = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function DumperLogTripPage() {
  const queryClient = useQueryClient();

  const { data: clients } = useQuery({
    queryKey: ['dumper-contracts'],
    queryFn: async () => (await apiClient.get<{ items: ClientRow[] }>('/api/v1/dumper-contracts')).data.items,
  });

  // Places used on previous loads, most-used first. One small request; the
  // browser filters it as you type via <datalist>.
  const { data: places } = useQuery({
    queryKey: ['dumper-locations'],
    queryFn: async () =>
      (await apiClient.get<{ items: { name: string }[] }>('/api/v1/dumper-trips/locations')).data.items,
  });

  const [plate, setPlate] = useState('');
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [driverName, setDriverName] = useState<string | null>(null);
  const [plateError, setPlateError] = useState<string | null>(null);

  const [clientId, setClientId] = useState('');
  const [rate, setRate] = useState<string>('');     // SAR/ton shown
  const [contractId, setContractId] = useState<string | null>(null);
  const [loadType, setLoadType] = useState('');

  const [tripDate, setTripDate] = useState(today());
  const [ticket, setTicket] = useState('');
  const [netKg, setNetKg] = useState('');

  // Trip start / end. datetime-local gives a value with no timezone, so it is
  // read as the browser's local time and sent as a full ISO instant.
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');

  // Where it started and ended. Free text with past entries offered as
  // suggestions - a fixed list would block a load whose site is not on it yet.
  const [startPlace, setStartPlace] = useState('');
  const [endPlace, setEndPlace] = useState('');

  // Plate type-ahead. Suggestions are advisory: the truck is only really
  // selected once resolvePlate() confirms it, so OCR and manual typing keep
  // behaving exactly as before.
  const [suggestions, setSuggestions] = useState<PlateSuggestion[]>([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const plateBoxRef = useRef<HTMLDivElement | null>(null);
  const suppressNextSearch = useRef(false);

  // Client picker. The contract list is already loaded, so this filters in the
  // browser - no request per keystroke.
  const [clientQuery, setClientQuery] = useState('');
  const [clientOpen, setClientOpen] = useState(false);
  const [clientHighlight, setClientHighlight] = useState(0);
  const clientBoxRef = useRef<HTMLDivElement | null>(null);

  const [resolving, setResolving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);

  // OCR pre-fill (D3)
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [storagePath, setStoragePath] = useState<string | null>(null);
  const [ocrState, setOcrState] = useState<'idle' | 'uploading' | 'reading' | 'done' | 'failed'>('idle');
  const [ocrConf, setOcrConf] = useState<number | null>(null);

  const tons = useMemo(() => (netKg ? Number(netKg) / 1000 : 0), [netKg]);
  const revenue = useMemo(() => (rate ? tons * Number(rate) : 0), [tons, rate]);

  /** "2026-08-04T06:00" -> full ISO instant, or null when blank. */
  const toIso = (local: string) => {
    if (!local.trim()) return null;
    const d = new Date(local);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };

  // Duration preview. Negative means end is before start, which we block.
  const durationMin = useMemo(() => {
    const s = toIso(startAt); const e = toIso(endAt);
    if (!s || !e) return null;
    return Math.round((new Date(e).getTime() - new Date(s).getTime()) / 60000);
  }, [startAt, endAt]);

  const durationText = durationMin == null
    ? null
    : durationMin < 0
      ? 'End is before start'
      : `${Math.floor(durationMin / 60)}h ${String(durationMin % 60).padStart(2, '0')}m`;

  function applyClient(cid: string) {
    setClientId(cid);
    const c = clients?.find((x) => x.client_id === cid);
    setRate(c?.rate_per_ton_sar ?? '');
    setContractId(c?.contract_id ?? null);
    if (c?.load_type) setLoadType(c.load_type);
  }

  const selectedClient = useMemo(
    () => clients?.find((c) => c.client_id === clientId) ?? null,
    [clients, clientId],
  );

  // Only true once the request has come back empty - while loading, `clients`
  // is undefined and we must not flash the "no clients" state.
  const noClients = Array.isArray(clients) && clients.length === 0;

  // Contracted clients first - those are the ones with a rate ready to apply.
  const clientMatches = useMemo(() => {
    const all = clients ?? [];
    const q = clientQuery.trim().toLowerCase();
    const matched = q
      ? all.filter((c) =>
          c.name_en.toLowerCase().includes(q) || (c.load_type ?? '').toLowerCase().includes(q))
      : all;
    return [...matched].sort((a, b) => {
      const ra = a.rate_per_ton_sar != null ? 0 : 1;
      const rb = b.rate_per_ton_sar != null ? 0 : 1;
      return ra !== rb ? ra - rb : a.name_en.localeCompare(b.name_en);
    });
  }, [clients, clientQuery]);

  function pickClient(c: ClientRow) {
    applyClient(c.client_id);
    setClientQuery('');
    setClientOpen(false);
  }

  function onClientKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!clientOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') { e.preventDefault(); setClientOpen(true); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setClientHighlight((h) => (clientMatches.length ? (h + 1) % clientMatches.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setClientHighlight((h) => (clientMatches.length ? (h - 1 + clientMatches.length) % clientMatches.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const c = clientMatches[clientHighlight];
      if (c) pickClient(c);
    } else if (e.key === 'Escape') {
      setClientOpen(false);
      setClientQuery('');
    }
  }

  // Debounced lookup as the operator types.
  useEffect(() => {
    if (suppressNextSearch.current) { suppressNextSearch.current = false; return; }
    const q = plate.trim();
    if (q.length < 1) { setSuggestions([]); setSuggestOpen(false); return; }
    let cancelled = false;
    setSuggestLoading(true);
    const timer = setTimeout(async () => {
      try {
        const r = await apiClient.get<{ items: PlateSuggestion[] }>(
          '/api/v1/dumper-trips/search-plate', { params: { q } },
        );
        if (cancelled) return;
        setSuggestions(r.data.items ?? []);
        setHighlight(0);
        setSuggestOpen((r.data.items ?? []).length > 0);
      } catch {
        if (!cancelled) { setSuggestions([]); setSuggestOpen(false); }
      } finally {
        if (!cancelled) setSuggestLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); setSuggestLoading(false); };
  }, [plate]);

  // Close the dropdown when focus leaves the whole plate control.
  useEffect(() => {
    function onDocDown(e: MouseEvent) {
      if (plateBoxRef.current && !plateBoxRef.current.contains(e.target as Node)) setSuggestOpen(false);
      if (clientBoxRef.current && !clientBoxRef.current.contains(e.target as Node)) {
        setClientOpen(false);
        setClientQuery('');
      }
    }
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  function pickSuggestion(s: PlateSuggestion) {
    suppressNextSearch.current = true;   // selecting must not reopen the list
    setPlate(s.plateEn);
    setSuggestOpen(false);
    setSuggestions([]);
    void resolvePlate(s.plateEn);
  }

  function onPlateKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!suggestOpen || suggestions.length === 0) {
      if (e.key === 'Enter') resolvePlate();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pickSuggestion(suggestions[highlight]);
    } else if (e.key === 'Escape') {
      setSuggestOpen(false);
    }
  }

  async function resolvePlate(plateArg?: string) {
    const p = (plateArg ?? plate).trim();
    if (p.length < 2) return;
    if (plateArg) setPlate(plateArg);
    setResolving(true); setPlateError(null);
    try {
      const r = (await apiClient.get<ResolveResp>('/api/v1/dumper-trips/resolve-plate', { params: { plate: p } })).data;
      if (!r.vehicle) { setVehicleId(null); setDriverName(null); setPlateError('No truck with that plate.'); return; }
      setVehicleId(r.vehicle.id);
      setDriverName(r.driver?.name ?? null);
      if (r.lastClient) {
        setClientId(r.lastClient.id);
        setRate(r.lastClient.ratePerTonSar ?? '');
        setContractId(r.lastClient.contractId);
        if (r.lastClient.loadType) setLoadType(r.lastClient.loadType);
      }
    } catch {
      setPlateError('Lookup failed.');
    } finally {
      setResolving(false);
    }
  }

  async function handleTicketFile(file: File) {
    setOcrState('uploading'); setOcrConf(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = (await apiClient.post<{ ocrId: string; storagePath: string }>(
        '/api/v1/dumper-trips/ocr', fd, { headers: { 'Content-Type': 'multipart/form-data' } },
      )).data;
      setStoragePath(up.storagePath);
      setOcrState('reading');
      for (let i = 0; i < 20; i++) {
        await new Promise((res) => setTimeout(res, 1500));
        const r = (await apiClient.get<{ ocr: Record<string, string | null> }>(
          `/api/v1/dumper-trips/ocr/${up.ocrId}`,
        )).data.ocr;
        const status = r.status ?? '';
        if (status === 'extracted' || status === 'review_needed' || status === 'failed') {
          if (status === 'failed') { setOcrState('failed'); return; }
          // Pre-fill only. The operator still glances and corrects before saving.
          if (r.ticket_number) setTicket(String(r.ticket_number));
          if (r.net_weight_kg) setNetKg(String(r.net_weight_kg));
          if (r.load_type && !loadType) setLoadType(String(r.load_type));
          setOcrConf(r.overall_confidence != null ? Number(r.overall_confidence) : null);
          if (r.vehicle_plate) await resolvePlate(String(r.vehicle_plate));
          setOcrState('done');
          return;
        }
      }
      setOcrState('done'); // timed out — keep whatever filled, continue manually
    } catch {
      setOcrState('failed');
    }
  }

  async function save() {
    if (!vehicleId || !clientId) return;
    setSaving(true);
    try {
      const r = (await apiClient.post<{ code: string; revenueSar: string | null; netWeightTons: string | null }>(
        '/api/v1/dumper-trips',
        {
          clientId,
          contractId: contractId ?? undefined,
          vehicleId,
          tripDate,
          loadType: loadType.trim() || null,
          ticketNumber: ticket.trim() || null,
          netWeightKg: netKg ? Number(netKg) : null,
          ratePerTonSar: rate ? Number(rate) : undefined,
          scaleTicketPhotoUrl: storagePath ?? undefined,
          tripStartedAt: toIso(startAt) ?? undefined,
          tripEndedAt: toIso(endAt) ?? undefined,
          tripStartLocation: startPlace.trim() || null,
          tripEndLocation: endPlace.trim() || null,
        },
      )).data;
      setLastSaved(`${r.code}: ${Number(r.netWeightTons ?? 0).toFixed(2)} t = ${Number(r.revenueSar ?? 0).toLocaleString()} SAR`);
      // keep truck + client sticky; clear the per-ticket fields. The two places
      // stay too: a truck runs the same route all day, so re-typing them for
      // every load is exactly the friction this page exists to remove.
      setTicket(''); setNetKg(''); setStoragePath(null); setOcrState('idle'); setOcrConf(null);
      setStartAt(''); setEndAt('');
      // a place typed for the first time should be suggested on the next load
      void queryClient.invalidateQueries({ queryKey: ['dumper-locations'] });
    } catch (err: any) {
      // Show the server's actual reason (e.g. "enter the net weight…") instead
      // of a generic message, so the operator knows exactly what to fix.
      setLastSaved(err?.response?.data?.message ?? 'Save failed — check the inputs.');
    } finally {
      setSaving(false);
    }
  }

  // A negative duration is always a typo, so block the save rather than store it.
  const canSave = !!vehicleId && !!clientId && !!netKg && !saving
    && (durationMin == null || durationMin >= 0);

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display text-lg">Dumpers · Log a load</div>
          <div className="flex items-center gap-2">
            <Link to="/dashboard/dumpers/loads" className="text-sm text-muted-foreground hover:text-foreground">All loads</Link>
            <Button variant="ghost" size="sm" onClick={() => { window.location.href = '/dashboard'; }} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Dumpers home
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-8">
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          {/* OCR scale ticket */}
          <div className="rounded-lg border border-dashed border-border p-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleTicketFile(f); e.currentTarget.value = ''; }}
            />
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm">
                <div className="font-medium flex items-center gap-2"><ScanLine className="h-4 w-4" /> Scan a scale ticket</div>
                <div className="text-xs text-muted-foreground">OCR pre-fills ticket #, plate and net weight. Always glance before saving.</div>
              </div>
              <Button variant="outline" size="sm" disabled={ocrState === 'uploading' || ocrState === 'reading'} onClick={() => fileRef.current?.click()}>
                {ocrState === 'uploading' || ocrState === 'reading'
                  ? (<span className="inline-flex items-center gap-1"><Loader2 className="h-4 w-4 animate-spin" /> Reading…</span>)
                  : 'Upload photo'}
              </Button>
            </div>
            {ocrState === 'done' && (
              <div className="text-xs mt-2 text-emerald-600">
                Pre-filled from the ticket{ocrConf != null ? ` (confidence ${(ocrConf * 100).toFixed(0)}%)` : ''}. Check every field below.
              </div>
            )}
            {ocrState === 'failed' && (
              <div className="text-xs mt-2 text-amber-600">Could not read the ticket — enter the fields manually below.</div>
            )}
          </div>

          {/* Plate — type-ahead over the fleet */}
          <div ref={plateBoxRef} className="relative">
            <label className="text-xs text-muted-foreground">Truck plate</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  className={inputCls}
                  value={plate}
                  onChange={(e) => setPlate(e.target.value)}
                  onKeyDown={onPlateKeyDown}
                  onFocus={() => { if (suggestions.length) setSuggestOpen(true); }}
                  // Only auto-resolve on blur when the operator is not clicking
                  // a suggestion - pickSuggestion resolves by itself.
                  onBlur={() => { setTimeout(() => { if (!suggestOpen) resolvePlate(); }, 120); }}
                  placeholder="Start typing: 577, KTA, VEH-0022…"
                  role="combobox"
                  aria-expanded={suggestOpen}
                  aria-autocomplete="list"
                  aria-controls="plate-suggestions"
                  autoComplete="off"
                  autoFocus
                />
                {suggestLoading && (
                  <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-3 text-muted-foreground" />
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => resolvePlate()} disabled={resolving} className="gap-1">
                <Search className="h-4 w-4" /> Find
              </Button>
            </div>

            {suggestOpen && suggestions.length > 0 && (
              <ul
                id="plate-suggestions"
                role="listbox"
                className="absolute z-20 left-0 right-0 mt-1 max-h-80 overflow-y-auto rounded-md border border-border bg-card shadow-lg"
              >
                {suggestions.map((s, i) => (
                  <li
                    key={s.id}
                    role="option"
                    aria-selected={i === highlight}
                    onMouseEnter={() => setHighlight(i)}
                    onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                    className={`cursor-pointer px-3 py-2 border-b border-border last:border-b-0 ${
                      i === highlight ? 'bg-muted' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono font-medium">{s.plateEn}</span>
                      <span className="text-[11px] text-muted-foreground">
                        {s.loadCount > 0 ? `${s.loadCount} load${s.loadCount === 1 ? '' : 's'}` : 'no loads yet'}
                      </span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Truck className="h-3 w-3" /> {s.code} · {s.vehicleType.replace(/_/g, ' ')}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <User className="h-3 w-3" /> {s.driverName ?? 'no driver'}
                      </span>
                      {s.lastClient && (
                        <span>last: {s.lastClient}{s.lastTripDate ? ` · ${s.lastTripDate}` : ''}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {plateError && <div className="text-xs text-rose-500 mt-1">{plateError}</div>}
            {vehicleId && (
              <div className="text-xs text-emerald-600 mt-1">
                Truck found{driverName ? ` · driver: ${driverName}` : ' · no driver assigned'}
              </div>
            )}
          </div>

          {/* Client + rate */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div ref={clientBoxRef} className="relative">
              <label className="text-xs text-muted-foreground">Client</label>
              {noClients ? (
                // Nothing to pick from: a dumper client with a rate has to be
                // created first, so send the operator straight there instead of
                // showing an empty dropdown.
                <div className="rounded-md border border-dashed border-amber-500/50 bg-amber-500/5 p-3 space-y-2">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    No dumper clients yet. A load cannot be saved without one.
                  </p>
                  <Link to="/dashboard/dumpers/clients">
                    <Button type="button" variant="gold" size="sm" className="gap-1 w-full">
                      <Plus className="h-4 w-4" /> Add a client and rate
                    </Button>
                  </Link>
                </div>
              ) : (
              <>
              <div className="relative">
                <input
                  className={`${inputCls} pr-8`}
                  // While the list is open the box is a search field; when closed
                  // it just shows the chosen client.
                  value={clientOpen ? clientQuery : (selectedClient?.name_en ?? '')}
                  onChange={(e) => { setClientQuery(e.target.value); setClientHighlight(0); setClientOpen(true); }}
                  onFocus={() => { setClientQuery(''); setClientHighlight(0); setClientOpen(true); }}
                  onKeyDown={onClientKeyDown}
                  placeholder={selectedClient ? selectedClient.name_en : 'Search client…'}
                  role="combobox"
                  aria-expanded={clientOpen}
                  aria-autocomplete="list"
                  aria-controls="client-options"
                  autoComplete="off"
                />
                <ChevronDown
                  className="h-4 w-4 absolute right-2.5 top-3 text-muted-foreground pointer-events-none"
                  aria-hidden
                />
              </div>

              {clientOpen && (
                <ul
                  id="client-options"
                  role="listbox"
                  className="absolute z-20 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-card shadow-lg"
                >
                  {clientMatches.length === 0 && (
                    <li className="px-3 py-2 text-xs text-muted-foreground">
                      No client matches “{clientQuery}”.{' '}
                      <Link to="/dashboard/dumpers/clients" className="underline text-foreground">
                        Add a new one
                      </Link>
                    </li>
                  )}
                  {clientMatches.map((c, i) => (
                    <li
                      key={c.client_id}
                      role="option"
                      aria-selected={c.client_id === clientId}
                      onMouseEnter={() => setClientHighlight(i)}
                      onMouseDown={(e) => { e.preventDefault(); pickClient(c); }}
                      className={`cursor-pointer px-3 py-2 border-b border-border last:border-b-0 ${
                        i === clientHighlight ? 'bg-muted' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className={`text-sm ${c.client_id === clientId ? 'font-medium' : ''}`}>
                          {c.name_en}
                        </span>
                        <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                          {c.rate_per_ton_sar != null
                            ? `${Number(c.rate_per_ton_sar).toLocaleString()} SAR/t`
                            : 'no contract rate'}
                        </span>
                      </div>
                      {c.load_type && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">{c.load_type}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              </>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Rate / ton (SAR)</label>
              <input className={inputCls} value={rate} onChange={(e) => setRate(e.target.value)} inputMode="decimal" placeholder="from contract" />
            </div>
          </div>

          {/* Ticket + weight + date + load */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Net weight (kg)</label>
              <input className={inputCls} value={netKg} onChange={(e) => setNetKg(e.target.value)} inputMode="decimal" placeholder="off the scale ticket" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Scale ticket #</label>
              <input className={inputCls} value={ticket} onChange={(e) => setTicket(e.target.value)} placeholder="ARASCO ticket no." />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Trip date</label>
              <input type="date" className={inputCls} value={tripDate} onChange={(e) => setTripDate(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Load type</label>
              <input className={inputCls} value={loadType} onChange={(e) => setLoadType(e.target.value)} placeholder="sand…" />
            </div>
          </div>

          {/* Trip start → end */}
          <div className="rounded-lg border border-border p-3 space-y-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Loaded → unloaded</div>

            {/* Shared by both place boxes. Typing filters it; anything else is
                still accepted, so a new site never blocks the load. */}
            <datalist id="trip-places">
              {(places ?? []).map((p) => <option key={p.name} value={p.name} />)}
            </datalist>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-muted-foreground" htmlFor="trip-start">Loaded at</label>
                  <input
                    id="trip-start"
                    type="datetime-local"
                    className={inputCls}
                    value={startAt}
                    onChange={(e) => setStartAt(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground" htmlFor="trip-start-place">Loaded at (place)</label>
                  <input
                    id="trip-start-place"
                    list="trip-places"
                    className={inputCls}
                    value={startPlace}
                    maxLength={200}
                    onChange={(e) => setStartPlace(e.target.value)}
                    placeholder="where it loaded…"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-muted-foreground" htmlFor="trip-end">Unloaded at</label>
                  <input
                    id="trip-end"
                    type="datetime-local"
                    className={inputCls}
                    value={endAt}
                    min={startAt || undefined}
                    onChange={(e) => setEndAt(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground" htmlFor="trip-end-place">Unloaded at (place)</label>
                  <input
                    id="trip-end-place"
                    list="trip-places"
                    className={inputCls}
                    value={endPlace}
                    maxLength={200}
                    onChange={(e) => setEndPlace(e.target.value)}
                    placeholder="where it dumped…"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <div className="flex gap-2">
                <button
                  type="button"
                  className="underline text-muted-foreground hover:text-foreground"
                  onClick={() => setStartAt(nowLocal())}
                >
                  Start now
                </button>
                <button
                  type="button"
                  className="underline text-muted-foreground hover:text-foreground"
                  onClick={() => setEndAt(nowLocal())}
                >
                  End now
                </button>
              </div>
              {durationText && (
                <span className={durationMin != null && durationMin < 0 ? 'text-destructive' : 'text-muted-foreground'}>
                  Duration: <b className={durationMin != null && durationMin >= 0 ? 'text-foreground' : ''}>{durationText}</b>
                </span>
              )}
            </div>
          </div>

          {/* Live computed revenue */}
          <div className="rounded-lg bg-muted/40 px-4 py-3 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {tons ? `${tons.toFixed(2)} tons` : '— tons'} {rate ? `× ${Number(rate).toLocaleString()} SAR` : ''}
            </div>
            <div className="text-lg font-semibold">
              {revenue ? `${revenue.toLocaleString(undefined, { maximumFractionDigits: 2 })} SAR` : '—'}
              <span className="ml-2 text-xs font-normal text-muted-foreground">computed, not invoiced</span>
            </div>
          </div>

          <Button className="w-full" disabled={!canSave} onClick={save}>
            {saving ? 'Saving…' : 'Save load'}
          </Button>

          {lastSaved && (
            <div className="flex items-center gap-2 text-sm text-emerald-600">
              <CheckCircle2 className="h-4 w-4" /> {lastSaved}
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Truck and client stay selected so you can log the next ticket fast. OCR pre-fill comes next (D3).
        </p>
      </main>
    </div>
  );
}
